'use client';

import { useLocale } from '@/lib/i18n/use-locale';
import { useState } from 'react';

interface UrlFormProps {
  onSubmit: (data: {
    url: string;
    customCode?: string;
    expiresIn?: number;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
  }) => void;
  loading: boolean;
}

export function UrlForm({ onSubmit, loading }: UrlFormProps) {
  const [url, setUrl] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [expiresIn, setExpiresIn] = useState('');
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmTerm, setUtmTerm] = useState('');
  const [utmContent, setUtmContent] = useState('');
  const [utmPreset, setUtmPreset] = useState('manual');
  const { t } = useLocale();

  const normalizeUtmValue = (value: string) => value.replace(/\s+/g, '+');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      url,
      customCode: customCode || undefined,
      expiresIn: expiresIn ? parseInt(expiresIn) : undefined,
      utmSource: utmSource || undefined,
      utmMedium: utmMedium || undefined,
      utmCampaign: utmCampaign || undefined,
      utmTerm: utmTerm || undefined,
      utmContent: utmContent || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-6'>
      <div>
        <label
          htmlFor='url'
          className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
        >
          {t('urlForm.enterUrl')}
        </label>
        <input
          type='url'
          id='url'
          value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder='https://example.com/very-long-url'
          required
          className='w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
        />
      </div>

      <button
        type='button'
        onClick={() => setShowAdvanced(!showAdvanced)}
        className='text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition'
      >
        {showAdvanced ? `− ${t('urlForm.hideAdvanced')}` : `+ ${t('urlForm.showAdvanced')}`}
      </button>

      {showAdvanced && (
        <div className='space-y-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700'>
          <div>
            <label
              htmlFor='customCode'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
            >
              {t('urlForm.customCode')}
            </label>
            <input
              type='text'
              id='customCode'
              value={customCode}
              onChange={e => setCustomCode(e.target.value)}
              placeholder='my-brand'
              pattern='[a-zA-Z0-9_-]{3,20}'
              className='w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
            />
            <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
              {t('urlForm.customHint')}
            </p>
          </div>

          <div>
            <label
              htmlFor='expiresIn'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
            >
              {t('urlForm.expiration')}
            </label>
            <select
              id='expiresIn'
              value={expiresIn}
              onChange={e => setExpiresIn(e.target.value)}
              className='w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
            >
              <option value=''>Never expire</option>
              <option value='1'>1 hour</option>
              <option value='24'>24 hours</option>
              <option value='168'>7 days</option>
              <option value='720'>30 days</option>
            </select>
            <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
              {t('urlForm.neverExpire')}
            </p>
          </div>

          <div>
            <p className='text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              UTM Parameters (optional)
            </p>
            <div className='mb-3'>
              <label
                htmlFor='utmPreset'
                className='block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1'
              >
                Popular presets
              </label>
              <select
                id='utmPreset'
                value={utmPreset}
                onChange={e => {
                  const value = e.target.value;
                  setUtmPreset(value);
                  if (value === 'manual') {
                    setUtmSource('');
                    setUtmMedium('');
                    setUtmCampaign('');
                    setUtmTerm('');
                    setUtmContent('');
                    return;
                  }
                  const presets: Record<
                    string,
                    { source: string; medium: string; campaign: string }
                  > = {
                    newsletter: {
                      source: 'newsletter',
                      medium: 'email',
                      campaign: 'monthly-newsletter',
                    },
                    facebook: { source: 'facebook', medium: 'social', campaign: 'social-post' },
                    facebookads: {
                      source: 'facebook',
                      medium: 'paid-social',
                      campaign: 'facebook-ads',
                    },
                    instagram: { source: 'instagram', medium: 'social', campaign: 'social-post' },
                    x: { source: 'x', medium: 'social', campaign: 'social-post' },
                    linkedin: { source: 'linkedin', medium: 'social', campaign: 'social-post' },
                    googleads: { source: 'google', medium: 'cpc', campaign: 'search-ads' },
                    whatsapp: {
                      source: 'whatsapp',
                      medium: 'messaging',
                      campaign: 'whatsapp-share',
                    },
                    telegram: {
                      source: 'telegram',
                      medium: 'messaging',
                      campaign: 'telegram-share',
                    },
                    line: { source: 'line', medium: 'messaging', campaign: 'line-share' },
                    tiktokads: { source: 'tiktok', medium: 'paid-social', campaign: 'tiktok-ads' },
                    tiktoksocial: { source: 'tiktok', medium: 'social', campaign: 'tiktok-social' },
                  };
                  const preset = presets[value];
                  if (preset) {
                    setUtmSource(preset.source);
                    setUtmMedium(preset.medium);
                    setUtmCampaign(preset.campaign);
                    setUtmTerm('');
                    setUtmContent('');
                  }
                }}
                className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
              >
                <option value='manual'>Manual / None</option>
                <option value='newsletter'>Newsletter (email)</option>
                <option value='facebook'>Facebook (social)</option>
                <option value='facebookads'>Facebook Ads (paid social)</option>
                <option value='instagram'>Instagram (social)</option>
                <option value='x'>X / Twitter (social)</option>
                <option value='linkedin'>LinkedIn (social)</option>
                <option value='googleads'>Google Ads (cpc)</option>
                <option value='whatsapp'>WhatsApp (messaging)</option>
                <option value='telegram'>Telegram (messaging)</option>
                <option value='line'>Line Messenger (messaging)</option>
                <option value='tiktokads'>TikTok Ads (paid social)</option>
                <option value='tiktoksocial'>TikTok Social (social)</option>
              </select>
            </div>
            <div className='grid gap-3 sm:grid-cols-2'>
              <div>
                <label
                  htmlFor='utmSource'
                  className='block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1'
                >
                  utm_source
                </label>
                <input
                  type='text'
                  id='utmSource'
                  value={utmSource}
                  onChange={e => setUtmSource(normalizeUtmValue(e.target.value))}
                  placeholder='newsletter'
                  className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
                />
              </div>
              <div>
                <label
                  htmlFor='utmMedium'
                  className='block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1'
                >
                  utm_medium
                </label>
                <input
                  type='text'
                  id='utmMedium'
                  value={utmMedium}
                  onChange={e => setUtmMedium(normalizeUtmValue(e.target.value))}
                  placeholder='email'
                  className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
                />
                <p className='mt-1 text-[11px] text-gray-500 dark:text-gray-400'>
                  Channels: cpc (paid ads), email, social, organic, affiliate, referral
                </p>
              </div>
              <div>
                <label
                  htmlFor='utmCampaign'
                  className='block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1'
                >
                  utm_campaign
                </label>
                <input
                  type='text'
                  id='utmCampaign'
                  value={utmCampaign}
                  onChange={e => setUtmCampaign(normalizeUtmValue(e.target.value))}
                  placeholder='spring-launch'
                  className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
                />
                <p className='mt-1 text-[11px] text-gray-500 dark:text-gray-400'>
                  Descriptive names: spring_sale_2026, product_launch_x, newsletter_q1, black_friday
                </p>
              </div>
              <div>
                <label
                  htmlFor='utmTerm'
                  className='block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1'
                >
                  utm_term
                </label>
                <input
                  type='text'
                  id='utmTerm'
                  value={utmTerm}
                  onChange={e => setUtmTerm(normalizeUtmValue(e.target.value))}
                  placeholder='running-shoes'
                  className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
                />
                <p className='mt-1 text-[11px] text-gray-500 dark:text-gray-400'>
                  Target Keywords: running+shoes, marketing+software
                </p>
              </div>
              <div className='sm:col-span-2'>
                <label
                  htmlFor='utmContent'
                  className='block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1'
                >
                  utm_content
                </label>
                <input
                  type='text'
                  id='utmContent'
                  value={utmContent}
                  onChange={e => setUtmContent(normalizeUtmValue(e.target.value))}
                  placeholder='cta-button'
                  className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition'
                />
                <p className='mt-1 text-[11px] text-gray-500 dark:text-gray-400'>
                  Element descriptors: header_button, text_link, banner_ad_blue, video_ad
                </p>
              </div>
            </div>
            <p className='mt-2 text-xs text-gray-500 dark:text-gray-400'>
              UTMs are appended to your destination URL for campaign tracking.
            </p>
          </div>
        </div>
      )}

      <button
        type='submit'
        disabled={loading}
        className='w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 ease-in-out transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed'
      >
        {loading ? t('urlForm.shortening') : t('urlForm.shorten')}
      </button>
    </form>
  );
}
